/*
 * Low-voltage purchase verification.
 *
 * Fires when a customer adds a tagged product to the cart: the add-to-cart submit is intercepted,
 * the disclaimer is shown, and only once the customer confirms does the product actually go in.
 *
 * The popup deliberately does NOT live on the cart page. That page reloads itself a few seconds
 * after arrival, which destroyed the dialog while customers were still reading it. Product pages
 * load once and stay put.
 *
 * Every tagged product is confirmed individually. CleanLife's wording is singular — "this product
 * will be installed with a compatible system" — and they hold these orders for physical
 * verification, so a confirmation given for one fixture is not taken to cover the next one. The
 * variants a customer has confirmed are kept in localStorage; the order itself carries a single
 * cart attribute, matching the contract's "recorded on the order".
 *
 * Losing that localStorage means being asked again, never being skipped.
 *
 * Backstop: the cart page also gates the checkout button, so a cart that already holds a tagged
 * product from an earlier session still has to be confirmed before checkout.
 */
if (!customElements.get('low-voltage-verification')) {
  customElements.define(
    'low-voltage-verification',
    class LowVoltageVerification extends ModalElement {
      get shouldLock() {
        return true;
      }

      get attributeName() {
        return this.dataset.attribute;
      }

      get confirmed() {
        return this.dataset.confirmed === 'true';
      }

      get popupCheckbox() {
        return this.querySelector('[data-low-voltage-popup-confirm]');
      }

      get titleList() {
        return this.querySelector('[data-low-voltage-titles]');
      }

      get errorMessage() {
        return this.querySelector('[data-low-voltage-error]');
      }

      get receipt() {
        return document.querySelector('[data-low-voltage-receipt]');
      }

      /* Rendered on the cart page only; absent everywhere else. */
      get state() {
        return document.querySelector('[data-low-voltage-state]');
      }

      connectedCallback() {
        super.connectedCallback();

        this.querySelector('[data-low-voltage-submit]')?.addEventListener('click', this.onConfirm.bind(this), {
          signal: this.abortController.signal,
        });

        this.popupCheckbox?.addEventListener(
          'change',
          () => this.errorMessage?.setAttribute('hidden', ''),
          { signal: this.abortController.signal }
        );

        /* Capture phase so we run before the theme's own product-form submit handler. */
        document.addEventListener('submit', this.onSubmit.bind(this), {
          capture: true,
          signal: this.abortController.signal,
        });

        document.addEventListener('click', this.onClick.bind(this), {
          capture: true,
          signal: this.abortController.signal,
        });
      }

      /* Add to cart on a tagged product. Confirmation is per variant, not per cart. */
      onSubmit(event) {
        const form = event.target;
        if (!form?.matches?.('[data-low-voltage="true"]')) return;
        if (Shopify.designMode) return;

        const variantId = form.querySelector('[name="id"]')?.value;
        if (this.hasConfirmed(variantId)) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        this.pendingForm = form;
        this.pendingVariantId = variantId;
        this.prompt(form.dataset.lowVoltageTitle);
      }

      get confirmedVariants() {
        try {
          const stored = JSON.parse(localStorage.getItem(this.storageKey));
          return Array.isArray(stored) ? stored : [];
        } catch (error) {
          return [];
        }
      }

      get storageKey() {
        return 'cleanlife:low-voltage-confirmed';
      }

      hasConfirmed(variantId) {
        return Boolean(variantId) && this.confirmedVariants.includes(String(variantId));
      }

      remember(variantId) {
        if (!variantId) return;

        try {
          const variants = this.confirmedVariants;
          if (variants.includes(String(variantId))) return;
          variants.push(String(variantId));
          localStorage.setItem(this.storageKey, JSON.stringify(variants));
        } catch (error) {
          /* Private browsing or a full quota. The confirmation still reached the cart; the only
             consequence is being asked again for this product. */
        }
      }

      /* Checkout while the cart already holds an unconfirmed tagged product. */
      onClick(event) {
        if (!event.target.closest?.('[name="checkout"]')) return;

        const state = this.state;
        if (!state || state.dataset.cartTagged !== 'true') return;
        if (this.confirmed || Shopify.designMode) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        this.pendingCheckout = true;
        this.prompt();
      }

      prompt(title) {
        if (this.open) return;

        this.setTitle(title);
        if (this.popupCheckbox) this.popupCheckbox.checked = false;
        this.errorMessage?.setAttribute('hidden', '');
        this.show();
      }

      /* Names the product being added, so the client's "This product" wording has a referent.
         Omitted when gating checkout, where the cart may hold several. */
      setTitle(title) {
        const list = this.titleList;
        if (!list) return;

        list.innerHTML = '';

        if (!title) {
          list.setAttribute('hidden', '');
          return;
        }

        const item = document.createElement('li');
        item.textContent = title;
        list.appendChild(item);
        list.removeAttribute('hidden');
      }

      onConfirm() {
        if (!this.popupCheckbox?.checked) {
          this.errorMessage?.removeAttribute('hidden');
          this.popupCheckbox?.focus();
          return;
        }

        const button = this.querySelector('[data-low-voltage-submit]');
        button?.setAttribute('aria-busy', 'true');
        button?.setAttribute('aria-disabled', 'true');

        this.record('Yes')
          .then((ok) => {
            if (!ok) return;

            this.remember(this.pendingVariantId);
            this.pendingVariantId = null;

            this.dataset.confirmed = 'true';
            this.receipt?.removeAttribute('hidden');
            if (this.state) this.state.dataset.confirmed = 'true';
            this.hide();

            /* Resume whatever the customer was doing when we interrupted them. */
            if (this.pendingForm) {
              const form = this.pendingForm;
              this.pendingForm = null;
              form.submit();
            } else if (this.pendingCheckout) {
              this.pendingCheckout = false;
              document.getElementById('cart')?.submit();
            }
          })
          .finally(() => {
            button?.removeAttribute('aria-busy');
            button?.removeAttribute('aria-disabled');
          });
      }

      /* Write the confirmation onto the cart. Empty string removes the attribute entirely so the
         order is not stamped with a bare "No". */
      record(value) {
        const config = theme.utils.fetchConfig('json', 'POST');
        config.body = JSON.stringify({ attributes: { [this.attributeName]: value } });

        return fetch(theme.routes.cart_update_url, config)
          .then((response) => response.ok)
          .catch(() => false);
      }
    }
  );
}
